import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useTheme,
  useMediaQuery,
  CardContent,
  Card,
  Grid,
  InputAdornment,
  IconButton,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatchWrapper } from "../../../hooks";
import {
  defaultMarketCampaignQuickSendList,
  IAudienceListItem,
  IMarketCampaignAudience,
  IMarketCampaignQuickSendList,
} from "../../../redux/marketCampaign/marketCampaign.types";
import { RightPanel } from "../../../components/RightPanel";
import {
  CustomCheckbox,
  CustomTextField,
} from "../../../components/formsComponents";

import { Close } from "@mui/icons-material";
import { useDebounce } from "../../../hooks/useDebounce";
import { useMarketCampaignContext } from "../MarketCampaign.context-state";
import CustomSwitch from "../../../components/customSwitch/customSwitch";
import { extractHeaders } from "../../marketingCampaign/components/ExcelFileUplaodButtonAsyncV2";
import produce from "immer";
import { useTimezoneFormatter } from "../../../hooks/useTimezoneFormatter";

export interface IAudienceData {
  name: string;
  phone: string;
  check: boolean;
}

export interface IQuickSendData {
  uuid: string;
  check: boolean;
  data: IMarketCampaignQuickSendList["quick_send_list"]["selected_audience_list_operation"];
}

export const defaultQuickSendData: IQuickSendData = {
  uuid: "",
  check: false,
  data: defaultMarketCampaignQuickSendList["quick_send_list"][
    "selected_audience_list_operation"
  ],
};
interface IPreviewOrUpdateChoosenAudieneRPProps {
  open: boolean;
  onClose: () => void;
  mode: "VIEW_AUDIENCE" | "QUICK_SEND";
  data: IMarketCampaignAudience;
  quickSendData: IQuickSendData;
  onSaveAndClose: (quickSendData: IQuickSendData) => void;
  readonly: boolean;
}

export const PreviewOrUpdateChoosenAudieneRP: React.FC<
  IPreviewOrUpdateChoosenAudieneRPProps
> = ({
  open,
  onClose,
  mode,
  data: audienceData,
  quickSendData,
  onSaveAndClose,
  readonly,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
  const { ftDate } = useTimezoneFormatter();
  const {
    isEditable,
    state,
    draftSteps: draftStepData,
    marketCampaign: campaignBasicInfo,
    onStateChange,
  } = useMarketCampaignContext();

  const newQuickSendList =
    audienceData?.audience_list || ([] as IAudienceListItem[]);

  let tableHeaders = extractHeaders(newQuickSendList || []);

  if (!tableHeaders.includes("check")) {
    tableHeaders = ["check"].concat(tableHeaders);
  }

  const [updatableData, setUpdatableData] = useState<{
    quickSendData: IQuickSendData;
    rows: IAudienceListItem[];
  }>({
    quickSendData: defaultQuickSendData,
    rows: [],
  });

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(100);
  const [contactNumberSearch, setContactNumberSearch] = useState("");
  const [guestNameSearch, setGuestNameSearch] = useState("");

  const debouncedContactNumber = useDebounce(contactNumberSearch, 500);
  const debouncedGuestName = useDebounce(guestNameSearch, 500);

  const filteredLocalRows = React.useMemo(() => {
    return updatableData.rows.filter((row) => {
      const phoneMatch = debouncedContactNumber
        ? row.phone?.toString().includes(debouncedContactNumber)
        : true;
      const nameMatch = debouncedGuestName
        ? row.name?.toLowerCase().includes(debouncedGuestName.toLowerCase())
        : true;
      return phoneMatch && nameMatch;
    });
  }, [updatableData.rows, debouncedContactNumber, debouncedGuestName]);

  const paginatedRows = React.useMemo(() => {
    return filteredLocalRows.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [filteredLocalRows, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [debouncedContactNumber, debouncedGuestName]);

  const allChecked = React.useMemo(() => {
    if (!updatableData.rows) return;
    const allRows = [...updatableData.rows, ...updatableData.rows];
    return allRows.length > 0 && allRows.every((x) => x.check === 1);
  }, [updatableData.rows, updatableData.rows]);

  const handleChangePage = (event: any, newPage: any) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCheckBoxAll = (
    _: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    const newCheckValue = checked ? 1 : 0;
    setUpdatableData((prev) => ({
      ...prev,
      rows: prev.rows.map((row) => ({
        ...row,
        check: newCheckValue,
      })),
    }));
  };

  const handleCheckBoxV2 =
    (phone: string) =>
    (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setUpdatableData((prev) => ({
        ...prev,
        rows: prev.rows.map((row) =>
          String(row.phone).trim() === phone.trim()
            ? { ...row, check: checked }
            : row,
        ) as IAudienceListItem[],
      }));
    };

  const getPanelTitle = () => {
    if (mode === "QUICK_SEND") return "Quick Send Campaign List";
    return `Audience Details`;
  };

  const formatHeaderText = (header: string) => {
    return header
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const sortHeaders = (headers: string[]) => {
    const priorityHeaders = ["check", "phone", "name"];
    const otherHeaders = headers.filter(
      (header) => !priorityHeaders.includes(header),
    );

    return [
      ...priorityHeaders.filter((header) => headers.includes(header)),
      ...otherHeaders,
    ];
  };

  const renderCellContent = (row: any, header: string) => {
    const value = row[header];

    if (header === "check") {
      return (
        <CustomCheckbox
          checked={Boolean(row.check) || false}
          onChange={handleCheckBoxV2(row.phone)}
          disabled={!isEditable || readonly}
        />
      );
    }

    if (header.includes("date") || header.includes("followup_date")) {
      return ftDate(value);
    }

    return value;
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (/^\d*$/.test(value) && value.length <= 10) {
      setContactNumberSearch(value);
    }
  };

  useEffect(() => {
    if (!audienceData || !quickSendData) return;
    const phoneSet = new Set(
      quickSendData.check
        ? quickSendData.data.chek.map((p) => p.phone.replace(/\s+/g, ""))
        : quickSendData.data.uncheck.map((p) => p.phone.replace(/\s+/g, "")),
    );
    const rowsData = newQuickSendList.map((item) => ({
      ...item,
      check: phoneSet.has(item.phone.replace(/\s+/g, "")) ? 1 : 0,
    }));

    setUpdatableData({
      quickSendData: quickSendData,
      rows: quickSendData.check
        ? rowsData
        : rowsData.map((r) => ({ ...r, check: Number(!r.check) })),
    });
  }, [quickSendData, audienceData]);

  const handleSaveAndClose = () => {
    onSaveAndClose({
      ...updatableData.quickSendData,
      data: {
        ...updatableData.quickSendData.data,
        ...(updatableData.quickSendData.check
          ? {
              chek: updatableData.rows
                .filter((r) => r.check)
                .map((r) => ({ phone: r.phone })),
            }
          : {
              uncheck: updatableData.rows
                .filter((r) => !r.check)
                .map((r) => ({ phone: r.phone })),
            }),
      },
    });
  };

  return (
    <>
      <RightPanel
        open={open}
        heading={getPanelTitle()}
        onClose={onClose}
        width="100%"
        isWrappedWithForm
        hideCrossTopButton={true}
        actionButtons={() => {
          return (
            <Stack
              direction={"row"}
              spacing={1}
              justifyContent={"flex-end"}
              mb={6}
            >
              {!readonly && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveAndClose}
                  disabled={!isEditable}
                >
                  Save
                </Button>
              )}

              <Button variant="contained" color="primary" onClick={onClose}>
                Close
              </Button>
            </Stack>
          );
        }}
      >
        <Grid
          sx={{
            display: "flex",
            margin: "0px",
            justifyContent: "space-between",
            alignItems: "center",
            flexDirection: `${isDesktop ? "row" : "column"}`,
          }}
        >
          <Box
            sx={{
              paddingBottom: "10px",
              display: "flex",
              gap: 2,
              my: 1,
            }}
          >
            <CustomTextField
              label="Search by Phone"
              variant="outlined"
              fullWidth
              value={contactNumberSearch}
              onChange={handlePhoneNumberChange}
              // sx={{ minWidth: 250 }}
              sx={isDesktop ? { minWidth: 250 } : {}}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                maxLength: 10,
              }}
            />

            <CustomTextField
              label="Search by Name"
              variant="outlined"
              fullWidth
              value={guestNameSearch}
              onChange={(e) => {
                const value = e.target.value;

                setGuestNameSearch(value);
              }}
              sx={isDesktop ? { minWidth: 250 } : {}}
              InputProps={{
                endAdornment: guestNameSearch && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setGuestNameSearch("");
                      }}
                      edge="end"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 2,
                backgroundColor: theme.palette.primary.main,
                borderRadius: "4px",
                height: "40px",
              }}
            >
              <Typography variant="body1" color="white">
                {`${updatableData.rows.filter((r) => r.check).length} customers selected`}
              </Typography>
            </Box>

            {updatableData.quickSendData && !readonly && (
              <CustomSwitch
                name="isIncludeSelected"
                leftText="Include"
                rightText="Exclude"
                disabled={!isEditable}
                checked={updatableData.quickSendData.check}
                onChange={(e) => {
                  const newState = produce(
                    updatableData.quickSendData,
                    (draftState) => {
                      draftState.check = e.target.checked;
                      const userPhoneSet = new Set(
                        (updatableData.rows || []).map((user) => user.phone),
                      );
                      draftState.data = {
                        ...draftState.data,
                        chek: draftState.data.chek.filter(
                          (phone) => !userPhoneSet.has(phone.phone),
                        ),
                        uncheck: draftState.data.uncheck.filter(
                          (phone) => !userPhoneSet.has(phone.phone),
                        ),
                      };
                    },
                  );
                  setUpdatableData((prev) => ({
                    quickSendData: newState,
                    rows: prev.rows.map((r) => ({
                      ...r,
                      check: e.target.checked ? 0 : 1,
                    })),
                  }));
                }}
              />
            )}
          </Box>
        </Grid>

        {isDesktop ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "primary.main" }}>
                  {tableHeaders &&
                    sortHeaders(tableHeaders).map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          color: "#fff",
                          fontSize: "0.95rem",
                          fontWeight: 500,
                        }}
                      >
                        {header === "check" ? (
                          <CustomCheckbox
                            checked={allChecked}
                            onChange={handleCheckBoxAll}
                            disabled={!isEditable || readonly}
                          />
                        ) : (
                          formatHeaderText(header)
                        )}
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows?.map((row, index) => {
                  return (
                    <TableRow key={index}>
                      {tableHeaders &&
                        sortHeaders(tableHeaders).map((header) => (
                          <TableCell
                            sx={{
                              minWidth: `${
                                header === "check" || header === "id"
                                  ? "10px"
                                  : "150px"
                              }`,
                            }}
                            key={`${index}-${header}`}
                          >
                            {renderCellContent(row, header) || "--"}
                          </TableCell>
                        ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
              }}
              component="div"
              count={filteredLocalRows.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        ) : (
          <Box sx={{ display: { xs: "block", md: "none" }, mt: 2 }}>
            {paginatedRows?.map((row, index) => (
              <Card
                key={index}
                sx={{
                  boxShadow: 1,
                  borderRadius: 3,
                  p: 1,
                  mb: 2,
                  backgroundColor: "#fafafa",
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <CustomCheckbox
                      checked={Boolean(row.check) || false}
                      onChange={handleCheckBoxV2(row.phone)}
                      disabled={!isEditable || readonly}
                    />
                  </Box>

                  {tableHeaders && (
                    <Grid container spacing={2}>
                      {sortHeaders(tableHeaders)
                        .filter(
                          (header) => header !== "check" && header !== "id",
                        )
                        .map((header, index) => (
                          <Grid
                            key={header}
                            item
                            xs={6}
                            sx={{
                              mb: 2,
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              {formatHeaderText(header)}
                            </Typography>
                            <Typography variant="body1">
                              {renderCellContent(row, header)}
                            </Typography>
                          </Grid>
                        ))}
                    </Grid>
                  )}
                </CardContent>
              </Card>
            ))}

            <TablePagination
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
              }}
              component="div"
              count={filteredLocalRows.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        )}
      </RightPanel>
      {/* <LoadingDialog open={loading} /> */}
    </>
  );
};
